import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { useNavigate, useQuery } from '../../routes';
import { fromAccounts, fromOrder, fromPaymentMethod, fromSubscription, useAppDispatch } from '../../store';
import OrderConfirmationSection from './order-confirmation-section/OrderConfirmantionSection';
import { Page } from '../../components/page';
import { subscription } from '../../layout/routes';
import { Feature, PaymentMethodType, PlanInterval } from '../../models';
import { PaymentInformationSection } from '../../components';
import { LoadingProgress } from '../../layout';
import { useGetQuotaAndCycleFromPathParams } from '../../hook';
import { Panel } from '../../components/common/Panel';

const SUPPORTED_FEATURES = [
  Feature.TESTOPS_PLATFORM,
  Feature.PER_USER_KSE,
  Feature.UNLIMITED_ENGINE,
  Feature.TESTCLOUD_SESSION_WEB_DESKTOP,
  Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
  Feature.TESTCLOUD_SESSION_MOBILE_APP,
  Feature.TESTCLOUD_SESSION_LIVE_TESTING,
  Feature.VISUAL_TESTING_PRO,
];

const CheckoutPage = () => {
  const { get } = useQuery();
  const { replace, replaceQuery } = useNavigate();

  const accountId = Number(get('accountId'));
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPaidSubscription = useSelector(fromSubscription.hasPaidSubscription(accountId));
  const dispatch = useAppDispatch();
  const paymentInfoRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);

  const featuresQuotaAndCycle = useGetQuotaAndCycleFromPathParams(SUPPORTED_FEATURES);

  const hasMonthlyItem = SUPPORTED_FEATURES.some(
    feature => featuresQuotaAndCycle[feature]?.cycle === PlanInterval.MONTH,
  );

  useEffect(() => {
    if (pendingOrder && pendingOrder?.length > 0) {
      replace(subscription.path, replaceQuery({
        accountId,
      }));
    }
  }, [pendingOrder]);

  const fetchInfo = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetTermsAndConditionsAccepted()),
        dispatch(fromAccounts.doCheckPublicDomain(accountId)),
        dispatch(fromSubscription.doFetchSubscriptions({ accountId })),
        dispatch(fromOrder.doFetchAllOrder(accountId)),
        dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
        dispatch(fromAccounts.doCheckFirstThreeLicenses()),
      ]);
    }
  };

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethodType) => {
    dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(paymentMethod));
    if (PaymentMethodType.BANK_TRANSFER === paymentMethod) {
      dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(false));
    }
  };

  const savePaymentMethodAndBillingInfo = async () => {
    if (paymentInfoRef.current && paymentInfoRef.current.isEnableSave()) {
      await Promise.all([
        await paymentInfoRef.current.saveCardInformation(),
        await paymentInfoRef.current.saveBillingInformation(),
      ]);
    }
  };

  return (
    <Page
      id="payment-checkout"
      headerId="page.payment.checkout.header"
      fetchData={fetchInfo}
    >
      <Panel
        id="payment-checkout-panel"
        sx={theme => ({
          margin: theme.spacing(4, 0),
        })}
      >
        <Box
          id="payment-checkout-box"
          key="payment-checkout-box"
          display="flex"
          flexDirection="row"
          m={4}
          height="100%"
        >
          <Box
            id="payment-billing-information"
            display="flex"
            flexDirection="column"
            width="70%"
            height="95%"
            overflow="auto"
          >
            <PaymentInformationSection
              ref={paymentInfoRef}
              accountId={accountId}
              enableSave
              availableRenewal
              hasMonthlyItem={hasMonthlyItem}
              hasPaidSubscription={hasPaidSubscription}
              handleSelectPaymentMethod={handleSelectPaymentMethod}
              showCardInformation={false}
            />
          </Box>
          <Divider
            orientation="vertical"
            sx={theme => ({
              width: '1px',
              backgroundColor: theme.common.grey[700],
              height: '95%',
            })}
            flexItem
          />
          <Box
            id="payment-billing-information-separator"
            display="flex"
            flexDirection="column"
            width="30%"
            minWidth={theme => theme.spacing(40)}
            overflow="auto"
          >
            <OrderConfirmationSection
              handleSave={savePaymentMethodAndBillingInfo}
              accountId={accountId}
              loading={loading}
              setLoading={setLoading}
            />
          </Box>
        </Box>
      </Panel>
      {loading && (<LoadingProgress fullscreen />)}
    </Page>
  );
};

export default CheckoutPage;
